import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { cn } from '../../lib/cn';
import { canDeleteParcoursTemplate } from '../../lib/dossiers/parcoursDefaults';
import type { ParcoursFamily } from '../../lib/dossiers/parcoursDefaults';
import {
  countParcoursUsage,
  createFamily,
  createFolderTemplate,
  createFrise,
  deleteFamily,
  deleteFolderTemplate,
  deleteFrise,
  loadParcoursCatalog,
  renameFamily,
  renameFolderTemplate,
  renameFrise,
  replaceFolderNames,
  replaceFriseSteps,
  setDefaultFolder,
  setDefaultFrise,
  setFolderOffered,
  setFriseOffered,
} from '../../services/dossiers/friseService';

import { DossierFieldFlagsSection } from './DossierFieldFlagsSection';
import { JurisdictionsSection } from './JurisdictionsSection';
import { TocSection } from './SettingsToc';
import { DossierRegistrySection } from './DossierRegistrySection';

const catalogKey = ['dossier-parcours-catalog'] as const;

type Pane = 'frises' | 'trees';

export function DossierSettingsTab() {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: catalogKey, queryFn: loadParcoursCatalog });
  const usage = useQuery({ queryKey: ['dossier-parcours-usage'], queryFn: countParcoursUsage });
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pane, setPane] = useState<Pane>('frises');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [creatingFamily, setCreatingFamily] = useState(false);

  const families = catalog.data ?? [];
  const selected = families.find((family) => family.id === familyId) ?? families[0] ?? null;

  useEffect(() => {
    if (!pendingName || !catalog.data) return;
    const created = [...catalog.data].reverse().find((family) => family.name === pendingName);
    if (!created) return;
    setFamilyId(created.id);
    setPendingName(null);
    setPane('frises');
    setEditingId(null);
  }, [catalog.data, pendingName]);

  function refresh() {
    qc.invalidateQueries({ queryKey: catalogKey });
    qc.invalidateQueries({ queryKey: ['dossier-parcours-usage'] });
  }

  function run(action: () => Promise<void>) {
    setError(null);
    action().then(refresh).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : 'Erreur');
    });
  }

  function selectFamily(id: string) {
    setFamilyId(id);
    setPane('frises');
    setEditingId(null);
    setRenaming(false);
  }

  if (catalog.isLoading) {
    return <p className="text-sm text-cime-text-muted">Chargement…</p>;
  }
  if (catalog.error) {
    return <p className="text-sm text-red-600">{catalog.error.message}</p>;
  }

  const familyUse = selected ? (usage.data?.families[selected.id] ?? 0) : 0;

  return (
    <div className="space-y-4">
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
      )}

      <TocSection label="Registre"><DossierRegistrySection /></TocSection>

      <div data-toc="Parcours" style={{ scrollMarginTop: 16 }} className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-cime border border-cime-border bg-cime-surface p-3">
          <h2 className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted">
            Parcours
          </h2>
          {creatingFamily ? (
            <FamilyCreate
              onCancel={() => setCreatingFamily(false)}
              onCreate={(name) => {
                setPendingName(name);
                setCreatingFamily(false);
                run(() => createFamily(name));
              }}
            />
          ) : (
            <Button type="button" className="mb-3 w-full" onClick={() => setCreatingFamily(true)}>
              + Nouveau parcours
            </Button>
          )}
          <div className="flex flex-col gap-1">
            {families.map((family) => {
              const on = selected?.id === family.id;
              const trees = family.folders.length
                ? `${family.folders.length} arborescence${family.folders.length > 1 ? 's' : ''}`
                : 'aucune arborescence';
              return (
                <button
                  key={family.id}
                  type="button"
                  onClick={() => selectFamily(family.id)}
                  className={cn(
                    'rounded-md px-3 py-2.5 text-left',
                    on
                      ? 'bg-cime-bg shadow-[inset_3px_0_0_var(--cime-gold)]'
                      : 'hover:bg-cime-bg',
                  )}
                >
                  <strong className={cn('block text-sm', on && 'text-cime-primary')}>{family.name}</strong>
                  <span className="mt-0.5 block text-xs text-cime-text-muted">
                    {family.frises.length} frise{family.frises.length > 1 ? 's' : ''} · {trees}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="rounded-cime border border-cime-border bg-cime-surface px-7 py-6">
          {selected ? (
            <FamilyPanel
              family={selected}
              dossiers={familyUse}
              pane={pane}
              setPane={(next) => { setPane(next); setEditingId(null); }}
              editingId={editingId}
              setEditingId={setEditingId}
              renaming={renaming}
              setRenaming={setRenaming}
              friseUse={(id) => usage.data?.frises[id] ?? 0}
              folderUse={(id) => usage.data?.folders[id] ?? 0}
              run={run}
            />
          ) : (
            <p className="text-sm text-cime-text-muted">Aucun parcours. Créez-en un pour commencer.</p>
          )}
        </section>
      </div>

      <TocSection label="Juridictions"><JurisdictionsSection /></TocSection>

      <TocSection label="Champs"><DossierFieldFlagsSection /></TocSection>
    </div>
  );
}

function FamilyCreate({ onCancel, onCreate }: { onCancel: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState('');
  return (
    <form
      className="mb-3 space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim()) onCreate(name.trim());
      }}
    >
      <Input label="Nom du parcours" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!name.trim()}>Créer</Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
}

function FamilyPanel({
  family, dossiers, pane, setPane, editingId, setEditingId, renaming, setRenaming,
  friseUse, folderUse, run,
}: {
  family: ParcoursFamily;
  dossiers: number;
  pane: Pane;
  setPane: (pane: Pane) => void;
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  renaming: boolean;
  setRenaming: (value: boolean) => void;
  friseUse: (id: string) => number;
  folderUse: (id: string) => number;
  run: (action: () => Promise<void>) => void;
}) {
  const canDelete = canDeleteParcoursTemplate(dossiers);
  const lede = dossiers === 0
    ? 'Aucun dossier ouvert sur ce parcours. Vous pouvez le supprimer.'
    : `${dossiers} dossier${dossiers > 1 ? 's ouverts' : ' ouvert'} sur ce parcours. Le supprimer est bloqué. Les dossiers déjà ouverts gardent la copie de leur frise.`;

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {renaming ? (
            <RenameFamily
              name={family.name}
              onCancel={() => setRenaming(false)}
              onSave={(name) => {
                setRenaming(false);
                if (name !== family.name) run(() => renameFamily(family.id, name));
              }}
            />
          ) : (
            <h2
              className="text-[34px] font-bold leading-tight text-cime-primary"
            >
              {family.name}
            </h2>
          )}
          <p className="mt-2 max-w-xl text-[13px] leading-snug text-cime-text-muted">{lede}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          {!renaming && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setRenaming(true)}>
              Renommer
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!canDelete}
            onClick={() => run(() => deleteFamily(family.id))}
          >
            Supprimer
          </Button>
        </div>
      </div>

      <div className="mt-5 inline-flex rounded-lg border border-cime-border bg-cime-bg p-0.5">
        <PaneButton on={pane === 'frises'} onClick={() => setPane('frises')}>
          Frises · {family.frises.length}
        </PaneButton>
        <PaneButton on={pane === 'trees'} onClick={() => setPane('trees')}>
          Arborescences · {family.folders.length}
        </PaneButton>
      </div>

      <div className="mt-4">
        {pane === 'frises' ? (
          <ItemList
            kind="frises"
            empty="Aucune frise. Un parcours sans frise proposée n’apparaît pas à la création d’un dossier."
            items={family.frises.map((frise) => ({
              id: frise.id,
              name: frise.name,
              def: frise.isDefault,
              offered: frise.offered,
              used: friseUse(frise.id),
              lines: frise.steps,
            }))}
            editingId={editingId}
            setEditingId={setEditingId}
            onCreate={(name, lines) => run(() => createFrise(family.id, name, lines))}
            onSave={(id, name, lines, previous) => run(async () => {
              if (name !== previous.name) await renameFrise(id, name);
              if (lines.join('\n') !== previous.lines.join('\n')) await replaceFriseSteps(id, lines);
            })}
            onDefault={(id) => run(() => setDefaultFrise(family.id, id))}
            onToggle={(id, offered) => run(() => setFriseOffered(id, offered))}
            onDelete={(id) => {
              setEditingId(null);
              run(() => deleteFrise(id));
            }}
          />
        ) : (
          <ItemList
            kind="trees"
            empty="Aucune arborescence. À la création, le choix restera « Vide »."
            items={family.folders.map((folder) => ({
              id: folder.id,
              name: folder.name,
              def: folder.isDefault,
              offered: folder.offered,
              used: folderUse(folder.id),
              lines: folder.names,
            }))}
            editingId={editingId}
            setEditingId={setEditingId}
            onCreate={(name, lines) => run(() => createFolderTemplate(family.id, name, lines))}
            onSave={(id, name, lines, previous) => run(async () => {
              if (name !== previous.name) await renameFolderTemplate(id, name);
              if (lines.join('\n') !== previous.lines.join('\n')) await replaceFolderNames(id, lines);
            })}
            onDefault={(id) => run(() => setDefaultFolder(family.id, id))}
            onToggle={(id, offered) => run(() => setFolderOffered(id, offered))}
            onDelete={(id) => {
              setEditingId(null);
              run(() => deleteFolderTemplate(id));
            }}
          />
        )}
      </div>
    </>
  );
}

function PaneButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3.5 py-1.5 text-[13px] font-bold',
        on ? 'bg-cime-surface text-cime-primary' : 'text-cime-text-muted',
      )}
    >
      {children}
    </button>
  );
}

function RenameFamily({ name, onSave, onCancel }: { name: string; onSave: (name: string) => void; onCancel: () => void }) {
  const [draft, setDraft] = useState(name);
  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (draft.trim()) onSave(draft.trim());
      }}
    >
      <Input label="Nom du parcours" value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
      <Button type="submit" size="sm">Enregistrer</Button>
      <Button type="button" size="sm" variant="secondary" onClick={onCancel}>Annuler</Button>
    </form>
  );
}

interface CatalogItem {
  id: string;
  name: string;
  def: boolean;
  offered: boolean;
  used: number;
  lines: string[];
}

function ItemList({
  kind, empty, items, editingId, setEditingId, onCreate, onSave, onDefault, onToggle, onDelete,
}: {
  kind: Pane;
  empty: string;
  items: CatalogItem[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  onCreate: (name: string, lines: string[]) => void;
  onSave: (id: string, name: string, lines: string[], previous: CatalogItem) => void;
  onDefault: (id: string) => void;
  onToggle: (id: string, offered: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const noun = kind === 'frises' ? 'étapes' : 'dossiers';
  return (
    <>
      {items.length === 0 && editingId !== 'new' && (
        <p className="text-[13px] text-cime-text-muted">{empty}</p>
      )}
      {items.map((item) => {
        const open = editingId === item.id;
        return (
          <div key={item.id} className="mb-2 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setEditingId(open ? null : item.id)}
              className={cn(
                'grid w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-3 rounded-cime border bg-cime-surface px-3.5 py-3 text-left transition-colors hover:bg-cime-bg',
                open ? 'border-cime-primary' : 'border-cime-border',
              )}
            >
              <span>
                <strong className="block text-sm">{item.name}</strong>
                <span className="mt-1 block text-xs text-cime-text-muted">
                  {item.lines.length} {noun} · {preview(item.lines)}
                </span>
              </span>
              <span className="flex flex-wrap justify-end gap-1.5">
                {item.def && <Badge tone="gold">Défaut</Badge>}
                {item.offered ? <Badge tone="ok">Proposée</Badge> : <Badge tone="off">Masquée</Badge>}
              </span>
            </button>
            {open && (
              <ItemEditor
                kind={kind}
                item={item}
                onClose={() => setEditingId(null)}
                onSave={(name, lines) => onSave(item.id, name, lines, item)}
                onDefault={() => onDefault(item.id)}
                onToggle={() => onToggle(item.id, !item.offered)}
                onDelete={() => onDelete(item.id)}
              />
            )}
          </div>
        );
      })}
      {editingId === 'new' ? (
        <ItemEditor
          kind={kind}
          item={{
            id: 'new',
            name: '',
            def: false,
            offered: true,
            used: 0,
            lines: kind === 'frises' ? ['Ouvert', 'Terminé'] : [''],
          }}
          onClose={() => setEditingId(null)}
          onSave={(name, lines) => {
            onCreate(name, lines);
            setEditingId(null);
          }}
          onDefault={() => undefined}
          onToggle={() => undefined}
          onDelete={() => setEditingId(null)}
        />
      ) : (
        <button
          type="button"
          className="mt-1 px-0.5 py-2 text-[13px] font-bold text-cime-primary"
          onClick={() => setEditingId('new')}
        >
          + {kind === 'frises' ? 'Nouvelle frise' : 'Nouvelle arborescence'}
        </button>
      )}
    </>
  );
}

function ItemEditor({
  kind, item, onClose, onSave, onDefault, onToggle, onDelete,
}: {
  kind: Pane;
  item: CatalogItem;
  onClose: () => void;
  onSave: (name: string, lines: string[]) => void;
  onDefault: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [lines, setLines] = useState(item.lines.length ? item.lines : ['']);
  const isNew = item.id === 'new';
  const blocked = !canDeleteParcoursTemplate(item.used);
  const min = kind === 'frises' ? 2 : 1;

  function move(index: number, delta: number) {
    const next = index + delta;
    if (next < 0 || next >= lines.length) return;
    const copy = [...lines];
    const [line] = copy.splice(index, 1);
    copy.splice(next, 0, line ?? '');
    setLines(copy);
  }

  return (
    <form
      className="rounded-cime border border-cime-border bg-cime-surface p-4"
      onSubmit={(event) => {
        event.preventDefault();
        const cleaned = lines.map((line) => line.trim()).filter(Boolean);
        if (!name.trim() || cleaned.length < min) return;
        onSave(name.trim(), cleaned);
      }}
    >
      <Input label="Nom" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      <p className="mb-2 mt-3 text-xs font-bold text-cime-text-muted">
        {kind === 'frises' ? 'Étapes, dans l’ordre' : 'Dossiers à la racine, dans l’ordre'}
      </p>
      {lines.map((line, index) => (
        <div key={index} className="mb-1.5 flex items-center gap-2">
          <span className="flex flex-col">
            <button type="button" className="text-cime-text-muted" aria-label="Monter" onClick={() => move(index, -1)}>↑</button>
            <button type="button" className="text-cime-text-muted" aria-label="Descendre" onClick={() => move(index, 1)}>↓</button>
          </span>
          <input
            className="h-9 flex-1 rounded-md border border-cime-border px-2.5 text-sm text-cime-text-primary"
            value={line}
            onChange={(event) => setLines(lines.map((value, i) => (i === index ? event.target.value : value)))}
          />
          {kind === 'frises' && index === lines.length - 1 && (
            <em className="text-[11px] font-bold uppercase not-italic tracking-wide text-cime-gold-text">Fin</em>
          )}
          <button
            type="button"
            className="px-1 text-lg text-cime-text-muted"
            aria-label="Retirer"
            onClick={() => setLines(lines.filter((_, i) => i !== index))}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="py-2 text-[13px] font-bold text-cime-primary"
        onClick={() => setLines([...lines, kind === 'frises' ? 'Nouvelle étape' : 'Nouveau dossier'])}
      >
        + Ajouter une ligne
      </button>
      <p className="mt-2 text-xs leading-snug text-cime-text-muted">
        {kind === 'frises'
          ? 'La dernière étape marque la fin du dossier, sans le fermer : pour le sortir de la liste, on l’archive. Un dossier déjà ouvert garde sa copie : modifier ici ne réécrit pas les dossiers en cours.'
          : 'Liste plate, à la racine de Documents. Un dossier déjà ouvert ne change pas.'}
      </p>
      {!isNew && (
        <p className="mt-1 text-xs leading-snug text-cime-text-muted">
          {blocked
            ? `${item.used} dossier${item.used > 1 ? 's s’en servent' : ' s’en sert'}. La suppression est bloquée. Passez la ligne en « Masquée » pour la retirer des nouveaux dossiers.`
            : 'Aucun dossier ne s’en sert. La suppression est possible.'}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!name.trim()}>Enregistrer</Button>
        <Button type="button" size="sm" variant="secondary" onClick={onClose}>Fermer</Button>
        {!isNew && !item.def && (
          <Button type="button" size="sm" variant="secondary" onClick={onDefault}>Définir par défaut</Button>
        )}
        {!isNew && (
          <Button type="button" size="sm" variant="secondary" onClick={onToggle}>
            {item.offered ? 'Masquer' : 'Proposer à la création'}
          </Button>
        )}
        {!isNew && (
          <Button type="button" size="sm" variant="secondary" disabled={blocked} onClick={onDelete}>
            Supprimer
          </Button>
        )}
      </div>
    </form>
  );
}

function Badge({ tone, children }: { tone: 'gold' | 'ok' | 'off'; children: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide',
        tone === 'gold' && 'border border-cime-gold-border bg-cime-gold-bg text-cime-gold-text',
        tone === 'ok' && 'bg-cime-primary/10 text-cime-primary',
        tone === 'off' && 'border border-cime-border bg-cime-bg text-cime-text-muted',
      )}
    >
      {children}
    </span>
  );
}

function preview(lines: string[]): string {
  const clean = lines.map((line) => line.trim()).filter(Boolean);
  if (clean.length <= 2) return clean.join(' → ');
  return `${clean[0]} → … → ${clean[clean.length - 1]}`;
}
